/**
 * GenreSelect组件的中间件组件
 * 由于历史代码中引用GenreSelector品类筛选器组件代码多，在重构GenreSelect品类筛选器组件后（最新品类筛选器组件），无法直接替换GenreSelector组件引用的代码
 * 所以新加这个中间件组件，把页面引用GenreSelector组件传入的配置、监听的事件 拦截 转为换 新品类筛选器组件GenreSelect组件 需要的数据格式，从而实现品类筛选器重构
 */
import React, { useMemo, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { DropdownProps } from 'tea-component';
import GenreSelect from '@intelligence/components/GenreSelect';
import useGenreSourcesOptions from '@intelligence/components/GenreSelectMiddleware/_hooks/useGenreSourcesOptions';
import {
  getGenreSelectValueBySource,
  getMainGenresAndSubGenres,
} from '@intelligence/components/GenreSelectMiddleware/utils';
import { GENRE_SOURCES, SHORTCUTS } from '@intelligence/components/GenreSelect/constants';

import type { IBaseProps, IGenreRes } from '@intelligence/types/common';
import type {
  IGenreSource,
  IGenreSources,
  IGenreSelectValue,
  IGenreSourcesShortcuts,
} from '@intelligence/components/GenreSelect/types';
import type { Platform } from '@intelligence/components/GenreSelectMiddleware/enums';
import type { MainGenre, SubGenre, IGenreSelectorPayload } from '@intelligence/components/GenreSelectMiddleware/types';
import type { IGenreSourcesOptionsFetcher } from '@intelligence/components/GenreSelectMiddleware/_hooks/useGenreSourcesOptions';

// 品类筛选器实例类型
export type IGenreSelectMiddlewareInstance = {
  // 重置品类筛选器勾选状态
  reset: () => void;
};

interface IGenreSelectMiddlewareProps extends IBaseProps {
  platform?: Platform;
  isMarketProfilMobile?: boolean;
  // 是否禁用iegg品类数据源类型，默认不禁用
  disableIegg?: boolean;
  // 是否禁用annie品类数据源类型，默认不禁用
  disableAnnie?: boolean;
  // 是否禁用sensortower品类数据源类型，默认不禁用
  disableSensorTower?: boolean;
  /**
   * 是否开启品类快捷勾选功能，默认开启
   * 这个变量名是由于系统已有好多地方用到了，所以重构的时候也沿用了
   */
  disableShortcut?: boolean;
  genresOptionsDataLoading?: boolean;
  /**
   * 主、副品类Options集合
   * 在传入genresOptionsData的时候，即使配置了fetchAction的service方法，也不会调用fetchAction获取主、副品类Options集合了
   */
  genresOptionsData?: IGenreRes;
  // 获取主、副品类Options集合的service方法
  fetchAction?: IGenreSourcesOptionsFetcher;
  // 品类数据源类型列表
  sources?: IGenreSources;
  // 品类数据源类型
  source: IGenreSource;
  // 已勾选的主品类
  mainGenres: MainGenre[];
  // 已勾选的副品类
  subGenres: SubGenre[];
  // 第3层已勾选的品类
  level3Genres?: SubGenre[];
  // 第4层已勾选的品类
  level4Genres?: SubGenre[];
  // 第5层已勾选的品类
  level5Genres?: SubGenre[];
  onChange?: (payload: IGenreSelectorPayload) => void;
  onConfirm?: (payload: IGenreSelectorPayload) => void;
  // 弹出层对齐方式，参考Tea组件库Dropdown组件的placement属性
  overlayPlacement?: DropdownProps['placement'];
}

const GenreSelectMiddleware: React.ForwardRefRenderFunction<
IGenreSelectMiddlewareInstance,
Omit<IGenreSelectMiddlewareProps, 'ref'>
> = (props, ref) => {
  const {
    style,
    className,
    platform,
    isMarketProfilMobile,
    disableIegg = false,
    disableAnnie = false,
    disableSensorTower = false,
    disableShortcut = true,
    genresOptionsDataLoading,
    genresOptionsData,
    fetchAction,
    sources: sourcesProps = GENRE_SOURCES,
    source,
    mainGenres,
    subGenres,
    level3Genres,
    level4Genres,
    level5Genres,
    onChange,
    onConfirm,
    entityType,
    ...restProps
  } = props;
  // console.debug('GenreSelectMiddleware', props);
  const {
    // 品类数据源类型与主、副品类Options集合的map映射
    options,
  } = useGenreSourcesOptions({
    // 品类数据源类型集合
    sources: [],
    platform,
    isMarketProfilMobile,
    genresOptionsDataLoading,
    // 自定义品类数据源类型与主、副品类Options集合的map映射
    genresOptionsData,
    // 自定义获取品类数据源类型与主、副品类Options集合的map映射的service
    fetchAction,
    entityType,
  });

  // 品类数据源类型集合
  const sources = useMemo<IGenreSource[]>(() => {
    const sourcesTmp = Array.isArray(sourcesProps) && sourcesProps.length > 0 ? sourcesProps : GENRE_SOURCES;
    return sourcesTmp.filter((source) => {
      if (source === 'iegg' && disableIegg) return false;
      if (source === 'annie' && disableAnnie) return false;
      if (source === 'sensortower' && disableSensorTower) return false;
      return true;
    });
  }, [JSON.stringify(sourcesProps), disableIegg, disableAnnie, disableSensorTower]);

  // 品类快捷勾选按钮
  const shortcuts = useMemo<IGenreSourcesShortcuts>(() => {
    let shortcutsListObj = {};
    const ieggOptions = options?.iegg || [];
    const ieggShooter = ieggOptions?.find((item: any) => item.value === 'Shooter') || ({} as any);
    const ieggShooterGenreList = ieggShooter?.children || [];
    if (ieggShooterGenreList?.findIndex((item: any) => item.value === 'Tarkov-Like') > -1) {
      shortcutsListObj = SHORTCUTS;
    } else {
      const shortIeggList = SHORTCUTS.iegg || [];
      const newShortIeggList = shortIeggList?.filter((item) => item.value !== 'Tarkov-Like');
      shortcutsListObj = {
        ...SHORTCUTS,
        iegg: [...newShortIeggList],
      };
    }
    if (disableShortcut) return shortcutsListObj;
    return Object.keys(shortcutsListObj).reduce((acc, cur) => {
      return { ...acc, [cur]: [] };
    }, shortcutsListObj);
  }, [sources, disableShortcut, options]);

  // 组件value
  const [value, setValue] = useState<IGenreSelectValue>(() =>
    getGenreSelectValueBySource({ source, mainGenres, subGenres, level3Genres, level4Genres, level5Genres }),
  );

  /**
   * REMARK: GenreSelector品类筛选器组件内部实现 没有真正实现onChange，而是在onConfirm触发的同时，也会触发onChange
   * 但是GenreSelect品类筛选器组件内部真正实现了onChange，在勾选操作的同时就触发
   * 所以这里onChange的逻辑相当于在GenreSelector组件上新增的，后续需要可以利用起来，暂时先不实现任何逻辑
   */
  const handleChange = () => {};

  const handleConfirm = (value: IGenreSelectValue) => {
    const source = value?.source;
    const sourceOptions = options?.[source] || [];
    const { mainGenres, subGenres, level3Genres, level4Genres, level5Genres } = getMainGenresAndSubGenres(
      value,
      sourceOptions,
    );
    const payload = {
      source: value?.source,
      options: sourceOptions,
      mainGenres,
      subGenres,
      level3Genres,
      level4Genres,
      level5Genres,
    };
    onChange?.(payload);
    onConfirm?.(payload);
  };

  // 重置品类筛选器勾选状态，即组件的value，一般暴露给组件调用者 调用
  const reset = () => {};

  useEffect(() => {
    const valueTmp = getGenreSelectValueBySource({
      source,
      mainGenres,
      subGenres,
      level3Genres,
      level4Genres,
      level5Genres,
    });
    setValue(valueTmp);
  }, [source, mainGenres, subGenres, level3Genres, level4Genres, level5Genres]);

  useImperativeHandle(ref, () => ({ reset }));

  return (
    <GenreSelect
      {...restProps}
      style={style}
      className={className}
      // 品类数据源类型集合
      sources={sources}
      // 品类数据源类型与品类快捷勾选按钮集合的map映射
      shortcuts={shortcuts}
      // 品类数据源类型与主、副品类Options集合的map映射
      options={options}
      // 组件value
      value={value}
      /**
       * 组件change的回调，以下几个场景会触发
       * 1、修改勾选的品类的数据源类型
       * 2、勾选、取消勾选副品类
       * 3、批量勾选、批量取消勾选副品类（搜索状态下的全选、取消全选）
       * 4、全选、取消全选副品类
       * 5、勾选、取消勾选主品类
       * 6、批量勾选、批量取消勾选副品类（搜索状态下的全选、取消全选）
       * 7、全选、取消全选主品类
       * 8、修改主品类的relation
       * 9、点击品类快捷勾选按钮
       */
      onChange={handleChange}
      // 组件关闭弹窗时触发，onChange的操作不会触发
      onConfirm={handleConfirm}
    />
  );
};

export default forwardRef(GenreSelectMiddleware);
